import { useContext, useState } from 'react';

import { AlertBox, Button } from '@iwoca/orion';
import classnames from 'classnames';
import { Formik, Form, FormikProps } from 'formik';
import * as Yup from 'yup';

import { ExistingPackageLinksTable } from './ExistingPackageLinksTable/ExistingPackageLinksTable';
import styles from './PackageLinkPage.module.css';
import { PackageLink } from './PackageLinksPage.types';
import { usePackageLinks } from './usePackageLinks';
import { ActivationContext } from '../../../activation/Activation';
import {
  PricingType,
  fetchPostPayLinkPackage,
} from '../../../api/lending/edge';
import { TPricingTypes } from '../../../Buyer/PayLinkLanding/utils/PayLinkLanding.types';
import { Input } from '../../../components/Input/Input';
import { LockedButton } from '../../../components/LockedButton/LockedButton';
import { getIwocaPayMeUrl } from '../../../components/PayMeLink/getIwocaPayMeUrl';
import { PayMeLink } from '../../../components/PayMeLink/PayMeLink';
import { useSellerHandle } from '../../../hooks/useSellerHandle.hook';
import { useStateKey } from '../../../hooks/useStateKey.hook';
import { CheckoutPreview } from '../components/CheckoutPreview/CheckoutPreview';
import { PayLinkPricingFormField } from '../components/PayLinkPricingFormField/PayLinkPricingFormField';
import {
  Column,
  VerticalSplitLayout,
} from '../components/VerticalSplitLayout/VerticalSplitLayout';
import { usePayIn12 } from '../hooks/usePayIn12';
import payLinksPageStyles from '../PayLinks.module.css';
import { DEFAULT_12M_PRICING_OPTION } from '../PayLinksNavigation';
import { usePreview } from '../usePreview';

export const PackageLinkPage = () => {
  const { sellerHandle } = useSellerHandle();
  const { packageLinks, fetchPackageLinks } = usePackageLinks();
  const { previewValues, setPreviewValuesFromEvent, isModularPricing } =
    usePreview();

  const activePackageLinks = packageLinks.filter(
    (packageLink: PackageLink) => !packageLink.deleted_at,
  );

  if (!sellerHandle) return null;

  return (
    <div className={payLinksPageStyles.PayLinksPage}>
      <VerticalSplitLayout>
        <Column>
          <div className={payLinksPageStyles.leftSide}>
            <h1 className={payLinksPageStyles.title}>
              A reusable pay link for a specific amount
            </h1>

            {previewValues?.pricing_3m ? (
              <CreatePackageLinkPageForm
                onChange={setPreviewValuesFromEvent}
                fetchPackageLinks={fetchPackageLinks}
                sellerHandle={sellerHandle}
                pricingTypes={{
                  pricing_3m: previewValues.pricing_3m?.type as PricingType,
                  pricing_12m: previewValues.pricing_12m?.type as PricingType,
                }}
                isModularPricing={isModularPricing}
              />
            ) : null}
          </div>
        </Column>
        <Column>
          {!!previewValues.pricing_3m ? (
            <CheckoutPreview
              sellerName={previewValues.sellerName}
              amount={previewValues.amount}
              reference={previewValues.reference}
              pricingPromotions={{
                THREE_MONTHS: previewValues.pricing_3m.promotions,
                TWELVE_MONTHS: previewValues.pricing_12m?.promotions,
              }}
              representativeInterest={{
                THREE_MONTHS: previewValues.pricing_3m.representativeInterest,
                TWELVE_MONTHS:
                  previewValues.pricing_12m?.representativeInterest,
              }}
            />
          ) : (
            <div>Loading preview...</div>
          )}
        </Column>
      </VerticalSplitLayout>
      <div className={styles.horizontalContainer}>
        <h2 className={payLinksPageStyles.subTitle}>
          Your existing package links
        </h2>
        <p className={payLinksPageStyles.description}>
          We save your package links so you can easily reshare them with
          customers.
        </p>
        <ExistingPackageLinksTable
          existingPackageLinks={activePackageLinks}
          sellerHandle={sellerHandle}
        />
      </div>
    </div>
  );
};

const ERROR_MESSAGE =
  'Error creating pay link package. Please refresh the page and try again.';

const CreatePackageLinkPageForm = ({
  fetchPackageLinks,
  onChange,
  sellerHandle,
  pricingTypes,
  isModularPricing,
}: {
  fetchPackageLinks: () => void;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  sellerHandle?: string;
  pricingTypes: TPricingTypes;
  isModularPricing: boolean;
}) => {
  const { stateKey } = useStateKey();
  const [hasSubmitError, setHasSubmitError] = useState(false);
  const [payLinkPackageUrl, setPayLinkPackageUrl] = useState<string | null>(
    null,
  );
  const { isOnboarded } = useContext(ActivationContext);
  const { payIn12Enabled } = usePayIn12();

  const INITIAL_VALUES = {
    packageLinkName: '',
    amount: '',
    reference: '',
    pricing_3m: pricingTypes.pricing_3m,
    pricing_12m: pricingTypes.pricing_12m || DEFAULT_12M_PRICING_OPTION,
  };

  return (
    <Formik
      initialValues={INITIAL_VALUES}
      validationSchema={Yup.object().shape({
        packageLinkName: Yup.string().required(
          'Please provide a package link name',
        ),
        amount: Yup.number()
          .min(0)
          .required('Please provide a pay link amount'),
        reference: Yup.string()
          .max(50, 'Limit 50 characters reached')
          .required('Please provide a pay link reference'),
        pricing_3m: Yup.string().required('Please provide a pay link pricing'),
      })}
      onSubmit={async (values, actions) => {
        actions.setSubmitting(true);
        setHasSubmitError(false);
        try {
          const response = await fetchPostPayLinkPackage({
            stateKey: stateKey,
            reference: values.reference,
            amount: values.amount,
            name: values.packageLinkName,
            productPricing: {
              pricing_3m: values.pricing_3m,
              ...(payIn12Enabled && { pricing_12m: values.pricing_12m }),
            },
          });
          const payLinkPackageId = response.data.id;
          setPayLinkPackageUrl(
            getIwocaPayMeUrl(sellerHandle!, payLinkPackageId),
          );

          fetchPackageLinks();
        } catch (e) {
          setHasSubmitError(true);
        }
        actions.setSubmitting(false);
      }}
      onReset={() => {
        setPayLinkPackageUrl(null);
      }}
    >
      {({
        values,
        handleChange,
        resetForm,
      }: FormikProps<typeof INITIAL_VALUES>) => {
        return (
          <Form>
            <p
              className={classnames(
                payLinksPageStyles.description,
                styles.desktopDescription,
              )}
            >
              Package links are for a set amount, to take payments for a
              specific product or service. Start by giving your package link a
              name - this will only be seen by you.
            </p>
            <p
              className={classnames(
                payLinksPageStyles.description,
                styles.mobileDescription,
              )}
            >
              You set the amount, reference and terms tailored to a product or
              package - it can be used multiple times by any customer.
            </p>
            {hasSubmitError ? (
              <AlertBox variant="failure" className={styles.submitError}>
                {ERROR_MESSAGE}
              </AlertBox>
            ) : null}
            <div className={styles.packageLinkName}>
              <Input
                label="Package link name"
                name="packageLinkName"
                onChange={onChange}
                placeholder="Name"
                className={styles.configurationInput}
              />
            </div>

            <p
              className={classnames(
                payLinksPageStyles.description,
                styles.payLinkSubDescription,
                styles.desktopDescription,
              )}
            >
              Next, add an amount or reference to let customers know what the
              payment is for and how much it costs
            </p>
            <div className={styles.payLinkConfiguration}>
              <Input
                label="Order Amount"
                name="amount"
                type="number"
                onChange={onChange}
                placeholder="Amount"
                className={styles.configurationInput}
              />
              <Input
                label="Invoice Reference"
                name="reference"
                onChange={onChange}
                placeholder="Reference"
                className={styles.configurationInput}
              />
            </div>

            {isModularPricing ? (
              <PayLinkPricingFormField
                productPricing={{
                  pricing_3m: values.pricing_3m,
                  pricing_12m: values.pricing_12m,
                }}
                onChange={(e) => {
                  onChange(e);
                  handleChange(e);
                }}
              />
            ) : null}

            {payLinkPackageUrl == null ? (
              <div className={payLinksPageStyles.buttonWrapper}>
                {isOnboarded ? (
                  <Button
                    variant="primary"
                    type="submit"
                    className={styles.linkButton}
                  >
                    Create a package link
                  </Button>
                ) : (
                  <LockedButton />
                )}
              </div>
            ) : (
              <div className={payLinksPageStyles.payLinkContainer}>
                <PayMeLink payMeUrl={payLinkPackageUrl} />
                <div
                  className={payLinksPageStyles.createNewLinkButtonContainer}
                >
                  <Button
                    variant="secondary"
                    className={styles.linkButton}
                    onClick={() => resetForm()}
                  >
                    Create a new package link
                  </Button>
                </div>
              </div>
            )}
          </Form>
        );
      }}
    </Formik>
  );
};
